/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CloseOutlined } from '@ant-design/icons'
import { App, Modal, Tabs, Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { paragraphMedium, weightRegular } from 'src/components/css'
import Button from 'src/components/ts-components/Button'
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import {
  RegistrationSetupContextWrapper,
  useRegistrationSetupContext,
} from './context'
import GeneralSettings from './forms/GeneralSettings'
import Payments from './forms/Payments'
import Customization from './forms/Customization'
import EmailCustomization from './forms/EmailCustomization/index'
import Waiver from './forms/Waiver'
import TeamCustomForm from './forms/TeamCustomForm'
import PlayerCustomForm from './forms/PlayerCustomForm'
import UnsavedChangesModal from './forms/CustomForm/UnsavedChangesModal'
// import RegistrationForm from './forms/RegistrationForm'
import { Registration } from '@sportninja/common/types'
import { RegistrationTypes, Tax } from '@sportninja/common/types/Registration'
import { useModalScroll } from 'src/hooks/useModalScroll'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import { Link } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'

const labelById = {
  0: 'General Settings',
  1: 'Payments',
  2: 'Player Waivers',
  3: 'Team Custom Form',
  4: 'Player Custom Form',
  5: 'Header Customization',
  6: 'Email Customization',
  // 6: 'Additional Info', // Moved to Customization tab
  // 7: 'Registration Form',
}
const styles = css`
  .my-modal-body {
  }
  .my-modal-mask {
    backdrop-filter: blur(1px);
  }
  .my-modal-header {
    border-bottom: 1px dotted ${colors.FIERY_RED};
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
  }
  .my-modal-content {
    border: 1px solid #333;
  }
`
const classNames = {
  body: styles['my-modal-body'],
  mask: styles['my-modal-mask'],
  header: styles['my-modal-header'],
  footer: styles['my-modal-footer'],
  content: styles['my-modal-content'],
}
const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10005,
  },
  mask: {
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10005,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

type RegistrationSetupProps = {
  isRegistrationFormOpen: boolean
  setIsRegistrationFormOpen: (isRegistrationFormOpen: boolean) => void
  registration: Registration | null
  registrationTypes: RegistrationTypes
  availableTaxes: Tax[]
  scheduleId: string
  refetchRegistrationFn: React.MutableRefObject<() => void>
  refetchRegistrationInfoFn: React.MutableRefObject<
    (displayLoading?: boolean) => void
  >
  accountId: string
}

/**
 * Internal modal component that renders the step-based registration setup UI.
 * Handles tab navigation, form rendering, and submission logic.
 *
 * @param setIsRegistrationFormOpen - Function to control modal visibility
 * @param refetchRegistrationFn - Ref to function that refetches registration data
 * @param refetchRegistrationInfoFn - Ref to function that refetches registration info
 * @param isRegistrationFormOpen - Boolean indicating if modal is open
 * @param registrationVersion - Version number from registration.root_schedule.registration_version.
 *   When equal to 1 (legacy registration), Team and Player custom form tabs are hidden.
 */
const RegistrationSetupContent = ({
  setIsRegistrationFormOpen,
  refetchRegistrationFn,
  refetchRegistrationInfoFn,
  isRegistrationFormOpen,
  registrationVersion,
}: {
  setIsRegistrationFormOpen: (isRegistrationFormOpen: boolean) => void
  refetchRegistrationFn: React.MutableRefObject<
    (displayLoading?: boolean) => void
  >
  refetchRegistrationInfoFn: React.MutableRefObject<
    (displayLoading?: boolean) => void
  >
  isRegistrationFormOpen: boolean
  registrationVersion: number
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const {
    submitRegistration,
    state,
    isUpdated,
    refetchWaivers,
    apiWaivers,
    activeTab,
    setActiveTab,
  } = useRegistrationSetupContext()

  const { message } = App.useApp()

  // 🔄 isLegacyRegistration: determines if custom form tabs should be hidden
  const isLegacyRegistration = registrationVersion === 1

  // Refs for custom forms to check unsaved changes
  const teamFormRef = useRef<{ hasPendingChanges: () => boolean }>(null)
  const playerFormRef = useRef<{ hasPendingChanges: () => boolean }>(null)

  // Modal state for unsaved changes warning
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)

  useEffect(() => {
    if (isRegistrationFormOpen) {
      refetchWaivers()
    }
  }, [isRegistrationFormOpen, refetchWaivers])

  // Handle edge case: redirect to general settings if on disabled waiver tab
  useEffect(() => {
    if (activeTab === '2' && (!apiWaivers || apiWaivers.length === 0)) {
      setActiveTab('0')
    }
  }, [activeTab, apiWaivers])

  // Redirect away from Email Customization tab if both team and player registrations are disabled
  useEffect(() => {
    const bothDisabled =
      !state.enableTeamRegistration && !state.enablePlayerRegistration
    if (activeTab === '6' && bothDisabled) {
      setActiveTab('0')
    }
  }, [
    activeTab,
    state.enableTeamRegistration,
    state.enablePlayerRegistration,
    setActiveTab,
  ])

  // Callback to refresh registration data after form mutations
  const refreshRegistration = useCallback(
    () => refetchRegistrationInfoFn?.current?.(false),
    [refetchRegistrationInfoFn]
  )

  const getFormById = (id: string) => {
    switch (id) {
      case '0':
        return <GeneralSettings />
      case '1':
        return <Payments />
      case '2':
        return <Waiver />
      case '3':
        return (
          <TeamCustomForm
            ref={teamFormRef}
            onFormChange={refreshRegistration}
          />
        )
      case '4':
        return (
          <PlayerCustomForm
            ref={playerFormRef}
            onFormChange={refreshRegistration}
          />
        )
      case '5':
        return <Customization />
      case '6':
        return <EmailCustomization />
      // case '6':
      //   return <AdditionalInfo /> // Moved to Customization tab
      // case '7':
      //   return <RegistrationForm />
      default:
        return null
    }
  }

  // Execute the actual submit logic
  const executeSubmit = useCallback(async () => {
    try {
      await submitRegistration()
      message.success(
        isUpdated
          ? 'Registration Updated Successfully'
          : 'Registration Created Successfully'
      )
      await refetchRegistrationFn?.current?.(isUpdated ? false : true)
      setIsRegistrationFormOpen(false)
    } catch (error: any) {
      if (error?.invalid_fields) {
        if (error.invalid_fields.currency) {
          setActiveTab('1')
        }
        if (error.invalid_fields.payment_currency) {
          setActiveTab('1')
        }
        if (error.invalid_fields.payment_type) {
          setActiveTab('1')
        }
        return
      }
      message.error(error.message || 'Failed to Submit Registration')
    }
  }, [
    submitRegistration,
    message,
    isUpdated,
    refetchRegistrationFn,
    setIsRegistrationFormOpen,
    setActiveTab,
  ])

  // Handle main save button click with unsaved changes check
  const handleMainSave = useCallback(() => {
    const teamUnsaved = teamFormRef.current?.hasPendingChanges?.()
    const playerUnsaved = playerFormRef.current?.hasPendingChanges?.()
    const unsaved = teamUnsaved || playerUnsaved

    if (unsaved) {
      setShowUnsavedModal(true)
      return
    }

    executeSubmit()
  }, [executeSubmit, activeTab])

  const getFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: 50%;
          margin-left: auto;
        `}
      >
        <Button
          text='Cancel'
          onClick={() => setIsRegistrationFormOpen(false)}
          variant='secondary'
          disabled={state.loading}
        />
        <Button
          text='Save'
          onClick={handleMainSave}
          variant='primary'
          disabled={state.loading}
        />
      </div>
    )
  }, [setIsRegistrationFormOpen, state.loading, handleMainSave])

  const getHeader = useCallback(() => {
    return (
      <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
        Registration Settings
      </Text>
    )
  }, [])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 16px;
          cursor: ${state.loading ? 'not-allowed' : 'pointer'};
          opacity: ${state.loading ? 0.5 : 1};
        `}
      />
    )
  }, [state.loading])

  const handleModalClose = useCallback(() => {
    // // Prevent closing modal when on custom form tabs to avoid losing data
    // if (activeTab === '4' || activeTab === '5') {
    //   return
    // }
    setIsRegistrationFormOpen(false)
  }, [setIsRegistrationFormOpen])

  const tabItems = useMemo(() => {
    const items = [
      {
        label: labelById[0],
        key: '0',
        children: getFormById('0'),
        disabled: false,
      },
      {
        label: labelById[1],
        key: '1',
        children: getFormById('1'),
        disabled: false,
      },
      {
        label:
          apiWaivers && apiWaivers.length > 0 ? (
            labelById[2]
          ) : (
            <Tooltip
              title={
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_0}
                >
                  Waiver(s) are required for signature during registration{' '}
                  <br /> New waivers can be added via the{' '}
                  <Link
                    to={`${ROUTES.GENERAL_SETTINGS}?origin=waiver`}
                    css={css`
                      color: ${colors.PRIMARY};
                      cursor: pointer;
                      text-decoration: underline;
                      &:hover {
                        opacity: 0.8;
                      }
                    `}
                    target='_blank'
                  >
                    Account Settings
                  </Link>
                </Text>
              }
              zIndex={10006}
              placement='right'
            >
              {labelById[2]}
            </Tooltip>
          ),
        key: '2',
        children: getFormById('2'),
        disabled: !apiWaivers || apiWaivers.length === 0,
      },
    ]

    // Add Team Custom Form tab if team registration is enabled and not legacy registration
    if (!isLegacyRegistration && state.enableTeamRegistration) {
      items.push({
        label: labelById[3],
        key: '3',
        children: getFormById('3'),
        disabled: false,
      })
    }

    // Add Player Custom Form tab if player registration is enabled and not legacy registration
    if (!isLegacyRegistration && state.enablePlayerRegistration) {
      items.push({
        label: labelById[4],
        key: '4',
        children: getFormById('4'),
        disabled: false,
      })
    }

    // Add Customization tab as the last tab
    items.push({
      label: labelById[5],
      key: '5',
      children: getFormById('5'),
      disabled: false,
    })

    // Add Customize Email tab - enabled only if at least one registration type is enabled
    items.push({
      label: labelById[6],
      key: '6',
      children: getFormById('6'),
      disabled: !(
        state.enableTeamRegistration || state.enablePlayerRegistration
      ),
    })

    return items
  }, [
    apiWaivers,
    state.enableTeamRegistration,
    state.enablePlayerRegistration,
    isLegacyRegistration,
  ])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        /* background: ${colors.SECONDARY_400} !important; */
        height: ${isMobile ? '100vh' : 'auto'} !important;
        max-height: ${isMobile ? '100vh' : '90vh'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '1080px'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          max-height: ${isMobile ? '100vh' : '90vh'} !important;
          border-radius: ${isMobile ? '0' : '8px'};
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          max-height: calc(
            90vh - 120px
          ); /* Subtract header and footer height */
        }
        .ant-tabs-tabpane {
          background: ${colors.SECONDARY_300} !important;
        }
        .ant-tabs-nav {
          min-height: ${isMobile ? 'auto' : '600px'} !important;
        }
        .ant-tabs-tab {
          height: 40px !important;
        }
        /* if the tab is active, change the background color */
        .ant-tabs-tab-active {
          background-color: #26303e;
        }
        .ant-tabs-tab-btn {
          ${paragraphMedium}
          ${weightRegular}
        }
        .ant-tabs-tab-btn[aria-selected='true'] {
          color: ${colors.PRIMARY}!important;
        }
        .ant-tabs-tab-btn[aria-selected='false'] {
          color: ${colors.NEUTRAL_30}!important;
        }
        .ant-tabs-tab.ant-tabs-tab-disabled {
          color: ${colors.NEUTRAL_30}!important;
          cursor: not-allowed !important;
          opacity: 0.5 !important;
        }
      `}
      width={isMobile ? '100%' : 1080}
      closeIcon={closeIcon()}
      title={getHeader()}
      footer={getFooter()}
      open={true}
      onOk={handleModalClose}
      onCancel={handleModalClose}
      centered
      classNames={classNames}
      styles={modalStyles}
      maskClosable={false}
      keyboard={activeTab !== '3' && activeTab !== '4'}
    >
      <Tabs
        activeKey={activeTab}
        defaultActiveKey='0'
        onChange={(key) => setActiveTab(key)}
        tabPosition={isMobile ? 'top' : 'left'}
        css={css`
          display: flex;
          flex: 1;
          height: 100%;
          overflow: hidden;
          .ant-tabs-nav {
            @media (max-width: 768px) {
              width: 100%;
              min-height: auto !important;
              border-bottom: 1px solid ${colors.SOFT_STEEL};
              .ant-tabs-nav-list {
                width: 100%;
                display: flex;
                flex-wrap: nowrap;
                overflow-x: auto;
                scrollbar-width: none;
                -ms-overflow-style: none;
                &::-webkit-scrollbar {
                  display: none;
                }
              }

              .ant-tabs-nav-operations {
                display: none !important;
              }
            }
          }

          .ant-tabs-content {
            height: 100%;
            overflow: hidden;
          }

          .ant-tabs-content-holder {
            flex: 1;
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }

          .ant-tabs-tabpane {
            flex: 1;
            overflow-y: auto;
            padding: 16px;
            @media (max-width: 768px) {
              padding: 16px;
            }
          }

          .ant-tabs-tab {
            height: 40px !important;
            @media (max-width: 768px) {
              margin: 0 !important;
              padding: 8px 16px !important;
              flex: 0 0 auto;
              justify-content: center;
              min-width: 120px;
            }
          }

          .ant-tabs-tab-active {
            background-color: #26303e;
            @media (max-width: 768px) {
              border-bottom: 2px solid ${colors.PRIMARY} !important;
            }
          }

          .ant-tabs-tab-btn {
            ${paragraphMedium}
            ${weightRegular}
            @media (max-width: 768px) {
              text-align: center;
              width: 100%;
              white-space: nowrap;
            }
          }

          .ant-tabs-tab-btn[aria-selected='true'] {
            color: ${colors.PRIMARY}!important;
          }

          .ant-tabs-tab-btn[aria-selected='false'] {
            color: ${colors.NEUTRAL_30}!important;
          }

          .ant-tabs-content-holder {
            @media (max-width: 768px) {
              padding: 0;
              max-height: 100%;
              overflow: hidden;
              height: 100%;
            }
          }

          .ant-tabs-ink-bar {
            @media (max-width: 768px) {
              display: none;
            }
          }
        `}
        items={tabItems}
        className='tab-options'
      />

      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        isLoading={state.loading}
        error={null}
        onClose={() => setShowUnsavedModal(false)}
        onConfirm={async () => {
          setShowUnsavedModal(false)
          await executeSubmit()
        }}
      />
    </Modal>
  )
}

/**
 * Main registration setup modal component that manages the registration configuration UI.
 * Computes the registration version and passes it to child components to control
 * which tabs are displayed based on legacy vs modern registration systems.
 *
 * @param isRegistrationFormOpen - Controls modal visibility
 * @param setIsRegistrationFormOpen - Function to set modal visibility
 * @param registration - Registration data containing version information
 * @param registrationTypes - Available registration types
 * @param availableTaxes - Available tax configurations
 * @param scheduleId - ID of the schedule being configured
 * @param refetchRegistrationFn - Function to refetch registration data
 * @param refetchRegistrationInfoFn - Function to refetch registration info
 * @param accountId - Account ID for the registration
 */
const RegistrationSetup = ({
  isRegistrationFormOpen,
  setIsRegistrationFormOpen,
  registration,
  registrationTypes,
  availableTaxes,
  scheduleId,
  refetchRegistrationFn,
  refetchRegistrationInfoFn,
  accountId,
}: RegistrationSetupProps) => {
  const { openModal, closeModal } = useModalScroll()

  // 🔄 registrationVersion: extract version to determine legacy vs modern registration
  const registrationVersion =
    registration?.root_schedule?.registration_version ?? 0

  useEffect(() => {
    if (isRegistrationFormOpen && refetchRegistrationInfoFn?.current) {
      refetchRegistrationInfoFn?.current?.(false)
    }
    if (isRegistrationFormOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [closeModal, isRegistrationFormOpen, openModal, refetchRegistrationInfoFn])

  if (!isRegistrationFormOpen) {
    return null
  }

  return (
    <RegistrationSetupContextWrapper
      registration={registration}
      registrationTypes={registrationTypes}
      availableTaxes={availableTaxes}
      scheduleId={scheduleId}
      accountId={accountId}
      isRegistrationFormOpen={isRegistrationFormOpen}
    >
      <RegistrationSetupContent
        setIsRegistrationFormOpen={setIsRegistrationFormOpen}
        refetchRegistrationFn={refetchRegistrationFn}
        refetchRegistrationInfoFn={refetchRegistrationInfoFn}
        isRegistrationFormOpen={isRegistrationFormOpen}
        registrationVersion={registrationVersion}
      />
    </RegistrationSetupContextWrapper>
  )
}

export default RegistrationSetup
